import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Box, Cpu, Clock, User, Sparkles } from 'lucide-react';
import api from '../lib/api';
import Fighter3D from '../components/battle/Fighter3D';
import { RARITY_UI } from '../lib/rarity';
import { SkeletonCardGrid } from '../components/Skeleton';

const STATUS_META = {
  pending:    { label: 'Queued',     color: '#94a3b8', icon: Clock },
  queued:     { label: 'Queued',     color: '#38bdf8', icon: Clock },
  generating: { label: 'Generating', color: '#fbbf24', icon: Cpu },
  ready:      { label: 'Ready',      color: '#4ade80', icon: Box },
  failed:     { label: 'Failed',     color: '#f87171', icon: Clock },
};

/**
 * The community 3D Catalog — every species that's been unlocked
 * appears here (ready models get a live rotating preview; still-
 * generating models show a "brewing" state). Each card credits the
 * explorer who brought the species into the catalog.
 */
export default function Catalog3D() {
  const nav = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let dead = false;
    api.get('/catalog/3d').then(r => {
      if (dead) return;
      setEntries(r.data.catalog || []);
      setLoading(false);
    }).catch(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, []);

  return (
    <section className="max-w-4xl mx-auto px-4 pt-6 pb-24 space-y-6" data-testid="catalog-3d-root">
      <header className="flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          data-testid="catalog-3d-back"
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center"
          aria-label="Back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Community 3D Repository</p>
          <h1 className="font-serif text-3xl leading-tight">The Living Catalog</h1>
        </div>
      </header>

      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-fuchsia-500/10 via-cyan-500/5 to-transparent p-4 flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-fuchsia-300 mt-0.5 shrink-0" />
        <p className="text-xs text-white/80 leading-relaxed">
          Every 3D creature in this catalog was brought into WildQuest by a real explorer who photographed and identified it. Capture a new species and you'll join them — the model you unlock is used by every future battle for that species.
        </p>
      </div>

      {loading && (
        <SkeletonCardGrid tiles={6} cols={2} testid="catalog-3d-loading" />
      )}

      {!loading && entries.length === 0 && (
        <div className="py-16 text-center text-muted-foreground">
          <p className="text-sm">The catalog is empty.</p>
          <p className="text-xs mt-1">Photograph a creature to unlock the first 3D model.</p>
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="catalog-3d-grid">
          {entries.map(e => <CatalogCard key={e.species_id} entry={e} onOpen={() => setSelected(e)} />)}
        </div>
      )}

      {selected && <CatalogDetailModal entry={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}

function CatalogCard({ entry, onOpen }) {
  const sm = STATUS_META[entry.status] || STATUS_META.pending;
  const StatusIcon = sm.icon;
  const rarityColor = RARITY_UI[entry.rarity]?.color || '#94a3b8';

  return (
    <motion.button
      onClick={onOpen}
      whileHover={{ y: -2 }}
      data-testid={`catalog-card-${entry.species_id}`}
      className="text-left rounded-2xl overflow-hidden border border-white/10 bg-black/40 hover:border-accent/40 transition-colors group"
    >
      <div className="relative aspect-square bg-gradient-to-br from-slate-900 to-slate-800">
        {entry.status === 'ready' && entry.model_url ? (
          <Fighter3D modelUrl={entry.model_url} rarityColor={rarityColor} size={160} />
        ) : entry.thumbnail_url ? (
          <img src={entry.thumbnail_url} alt="" className="w-full h-full object-cover opacity-70" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Box className="w-8 h-8 text-white/20" />
          </div>
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] uppercase tracking-widest"
          style={{ background: `${sm.color}22`, color: sm.color, border: `1px solid ${sm.color}55` }}>
          <StatusIcon className="w-3 h-3" /> {sm.label}
        </div>
      </div>
      <div className="p-3">
        <p className="text-sm text-white/95 truncate">{entry.common_name || 'Unknown species'}</p>
        <p className="font-serif italic text-[11px] text-muted-foreground truncate">{entry.scientific_name}</p>
        <p className="text-[10px] text-muted-foreground mt-1 truncate">
          Unlocked by <span className="text-white/70">{entry.unlocked_by?.nickname || 'Explorer'}</span>
        </p>
      </div>
    </motion.button>
  );
}

function CatalogDetailModal({ entry, onClose }) {
  const sm = STATUS_META[entry.status] || STATUS_META.pending;
  const rarityColor = RARITY_UI[entry.rarity]?.color || '#94a3b8';
  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="catalog-3d-detail">
      <div className="w-full max-w-md rounded-3xl bg-[#0f1a15] border border-white/10 p-6 space-y-4"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-accent">{sm.label}</p>
            <h2 className="font-serif text-2xl leading-tight">{entry.common_name}</h2>
            <p className="font-serif italic text-xs text-muted-foreground">{entry.scientific_name}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800">
          {entry.status === 'ready' && entry.model_url ? (
            <Fighter3D modelUrl={entry.model_url} rarityColor={rarityColor} size={360} />
          ) : entry.thumbnail_url ? (
            <img src={entry.thumbnail_url} alt="" className="w-full h-full object-cover opacity-70" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Box className="w-16 h-16 text-white/20" />
            </div>
          )}
        </div>

        <div className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center gap-3">
          {entry.unlocked_by?.picture ? (
            <img src={entry.unlocked_by.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
              <User className="w-4 h-4 text-white/60" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Unlocked by</p>
            <p className="text-sm truncate">{entry.unlocked_by?.nickname || 'Anonymous Explorer'}</p>
          </div>
          {entry.unlocked_at && (
            <p className="text-[10px] text-muted-foreground shrink-0">
              {new Date(entry.unlocked_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
